"use client";

import { X, BookOpen, Bookmark, Crown, History } from "lucide-react";
import Link from "next/link";

type FeatureType = "errorNote" | "problemSave" | "problemHistory";

interface FeatureLockedModalProps {
  isOpen: boolean;
  featureType: FeatureType;
  onClose: () => void;
}

const featureInfo: Record<
  FeatureType,
  { title: string; icon: React.ReactNode; description: string[] }
> = {
  errorNote: {
    title: "오답노트",
    icon: <BookOpen className="w-6 h-6 text-blue-500" />,
    description: [
      "틀린 문제에 대한 메모를 작성하고 저장할 수 있어요",
      "나중에 복습할 때 왜 틀렸는지 쉽게 확인할 수 있어요",
      "오답 패턴을 분석해서 약점을 파악할 수 있어요",
    ],
  },
  problemSave: {
    title: "문제 저장",
    icon: <Bookmark className="w-6 h-6 text-yellow-500" />,
    description: [
      "중요한 문제를 북마크해서 저장할 수 있어요",
      "저장한 문제만 모아서 볼 수 있어요",
      "시험 전 핵심 문제 복습에 활용할 수 있어요",
    ],
  },
  problemHistory: {
    title: "풀이이력",
    icon: <History className="w-6 h-6 text-purple-500" />,
    description: [
      "문제별 정답 현황을 추적하고 실력 향상을 확인할 수 있어요",
      "문제를 얼마나 자주 풀었는지 확인할 수 있어요",
      "이력을 보고 패스할 문제와 집중할 문제를 구분할 수 있어요",
    ],
  },
};

export default function FeatureLockedModal({
  isOpen,
  featureType,
  onClose,
}: FeatureLockedModalProps) {
  if (!isOpen) return null;

  const feature = featureInfo[featureType];

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-start justify-center z-50 px-4 pt-16">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
        {/* 모달 헤더 */}
        <div className="flex items-center justify-between p-3 border-b dark:border-gray-700">
          <div className="flex items-center gap-2">
            {feature.icon}
            <h3 className="text-base md:text-lg font-semibold dark:text-white">
              {feature.title} 기능
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-4 h-4 dark:text-gray-300" />
          </button>
        </div>

        {/* 컨텐츠 영역 */}
        <div className="p-4">
          {/* 기능 미리보기 GIF */}
          {featureType === "problemHistory" && (
            <div className="mb-4 rounded-lg overflow-hidden border dark:border-gray-700">
              {/* GIF 미리보기 + 동적 width, 클릭 시 모달 표시라 LCP 영향 낮음 */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/feature-locked-modal/problem-history.gif"
                alt="풀이이력 기능 미리보기"
                className="w-full"
              />
            </div>
          )}
          {featureType === "errorNote" && (
            <div className="mb-4 rounded-lg overflow-hidden border dark:border-gray-700">
              {/* GIF 미리보기 + 동적 width, 클릭 시 모달 표시라 LCP 영향 낮음 */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/feature-locked-modal/error-note.gif"
                alt="오답노트 기능 미리보기"
                className="w-full"
              />
            </div>
          )}
          {featureType === "problemSave" && (
            <div className="mb-4 rounded-lg overflow-hidden border dark:border-gray-700">
              {/* GIF 미리보기 + 동적 width, 클릭 시 모달 표시라 LCP 영향 낮음 */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/feature-locked-modal/problem-save.gif"
                alt="문제 저장 기능 미리보기"
                className="w-full"
              />
            </div>
          )}

          <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 font-medium mb-2">
              <Crown className="w-5 h-5" />
              <span>프리미엄 전용 기능</span>
            </div>
            <p className="text-sm text-orange-700 dark:text-orange-300">
              이 기능은 프리미엄 이상 이용권에서 사용할 수 있습니다.
            </p>
          </div>

          <ul className="space-y-2">
            {feature.description.map((desc, index) => (
              <li
                key={index}
                className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"
              >
                <span className="text-green-500 mt-0.5">✓</span>
                <span>{desc}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 모달 푸터 */}
        <div className="flex justify-end gap-2 p-3 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            나중에
          </button>
          <Link
            href="/membership"
            onClick={onClose}
            className="px-4 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1.5"
          >
            <Crown className="w-3 h-3" />
            프리미엄 업그레이드
          </Link>
        </div>
      </div>
    </div>
  );
}
